import { useElementObserveSize } from "@msys/ui";
import { Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, {
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ScrollSync, ScrollSyncPane } from "react-scroll-sync";
import SortableTree, {
  ExtendedNodeData,
  getVisibleNodeCount,
  TreeItem,
} from "react-sortable-tree";
import { useLatest, useUpdateEffect } from "react-use";
import { WindowScroller } from "react-virtualized";
import { ItemType } from "../../clients/graphqlTypes.js";
import { color, size } from "../../common/MuiThemeProvider.js";
import { useScrollZone } from "../commons/hooks/useScrollZone.js";
import { TREE_SCAFFOLD_PX_WIDTH } from "../constants.js";
import { LocalDndProvider } from "../dnd.js";
import { browserHasOnlyTouch } from "../featureDetection.js";
import { getDefaultCreatableSubItemTypes } from "../features/doc-items/constraints.js";
import { transientOptions } from "../styles.js";
import { InputComponent, ItemComponent } from "../trees/types.js";
import { VirtualTableNodeRenderer } from "./components/VirtualTableNodeRenderer.js";
import {
  getLastSelectedNode,
  getMoveTreeItemRank,
  getNonLeafChildrenIdsRecurively,
  getSyntheticRootItem,
  getTreeFromItems,
  getTreeNodeKey,
  isNodePlacedAfterInput,
  ROOT_ITEM_ID,
} from "./helpers.js";
import { ALL_KEY, ExpandedStore } from "./hooks/useExpandedStore.js";
import { useWindowScrollerOffsetSync } from "./hooks/useWindowScrollerOffsetSync.js";
import {
  AdditionalItemInput,
  VirtualItem,
  VirtualItemTreeRef,
  VirtualTreeTableContext,
} from "./types.js";

const DEFAULT_STYLE = { height: "unset" };

interface Props<T extends VirtualItem> {
  minWidth?: number;

  // Drag-reorder move handler — owned by the parent (which holds the document
  // identity). When omitted, dragging produces no move.
  moveTreeItem?: (
    itemId: string,
    parentId: string,
    rank: number
  ) => Promise<void>;
  moveTreeItemLoading?: boolean;

  items: T[];
  allItems?: T[];
  filterItem?: (item: T) => boolean;

  // virtual synthetic root item or root item id
  rootItem?: T;
  rootItemId?: string;

  rootItemComponent?: ItemComponent<T>;
  itemComponent: ItemComponent<T>;
  inputComponent?: InputComponent<T, T>;

  selectedItemId: string | null;

  enableCreating: boolean;

  allowHaveChildren?: (item: T) => boolean;

  shouldRenderCreateInput?: (item: T) => boolean;

  documentItemTypes: ItemType[];

  container?: HTMLElement | null;

  isVirtualized?: boolean;

  onItemsCountChange?: (itemsCount: number) => void;

  HeaderRow?: JSX.Element | null;
  EmptyRow?: JSX.Element | null;

  headerTitle?: string | null;

  expandedStore: ExpandedStore;
  // isItemExpanded: (itemId: string, defaultValue?: boolean) => boolean;
  // setItemExpanded: (itemId: string, expanded: boolean) => void;
  // setExpandedState: (state: { [key: string]: boolean }) => void;

  additionalItemInput?: AdditionalItemInput | null;

  showExpandButton?: (item: T) => boolean;
}

export const VirtualTableTree = React.memo(
  React.forwardRef(VirtualTableTreeComponent)
);

function VirtualTableTreeComponent<T extends VirtualItem>(
  {
    moveTreeItem,
    moveTreeItemLoading = false,
    items,
    allItems,
    filterItem,
    rootItem: syntheticRootItem,
    rootItemId: syntheticRootItemId,
    selectedItemId,
    rootItemComponent,
    itemComponent,
    inputComponent,
    enableCreating,
    allowHaveChildren,
    shouldRenderCreateInput,
    documentItemTypes,
    container,
    isVirtualized = true,
    onItemsCountChange,
    minWidth = 1400,
    headerTitle,
    HeaderRow,
    EmptyRow,
    expandedStore,
    additionalItemInput,
    showExpandButton,
  }: // isItemExpanded,
  // setItemExpanded,
  // setExpandedState,
  Props<T>,
  forwardedRef: Ref<VirtualItemTreeRef>
) {
  const enableDragging = moveTreeItem != null;

  const [{ scrollbarHeight, containerWidth, outerContainerWidth }, setState] =
    useState<{
      scrollbarHeight: number;
      containerWidth: number;
      outerContainerWidth: number;
    }>({ scrollbarHeight: 0, containerWidth: 0, outerContainerWidth: 0 });

  const selectedItem = useMemo<T | null>(
    () => items.find(i => i.id === selectedItemId) ?? null,
    [items, selectedItemId]
  );

  // @ts-ignore synthetic root item support
  const rootItem: T | undefined = useMemo(() => {
    if (syntheticRootItem) return syntheticRootItem;
    if (syntheticRootItemId)
      return getSyntheticRootItem("Item", syntheticRootItemId);
    return items.find(item => item.isRootItem);
  }, [items, syntheticRootItem, syntheticRootItemId]);

  const getTree = () => {
    return getTreeFromItems(
      items,
      rootItem,
      selectedItem,
      filterItem,
      enableCreating,
      expandedStore.isItemExpanded,
      documentItemTypes,
      shouldRenderCreateInput,
      additionalItemInput
    );
  };

  const updateTree = () => {
    const treeData = getTree();
    setQuoteItems(treeData);
    return treeData;
  };

  const [quoteItems, setQuoteItems] = useState(getTree());

  const collapseItemAndChildrenRecursively = React.useCallback(
    (node: TreeItem) => {
      const toCollapseIds = getNonLeafChildrenIdsRecurively(node);
      const toExpandItemIds = expandedStore.areAllItemsExpanded
        ? items
            .filter(
              item => item.hasChildren && !toCollapseIds.includes(item.id)
            )
            .map(item => item.id)
        : [];
      expandedStore.setExpandedState(
        Object.fromEntries([
          [ALL_KEY, false],
          ...toExpandItemIds.map(id => [id, true]),
          ...toCollapseIds.map(id => [id, false]),
        ])
      );
    },
    [expandedStore, items]
  );

  useUpdateEffect(() => {
    updateTree();
  }, [
    items,
    filterItem,
    rootItem?.id,
    selectedItem?.id,
    expandedStore.isItemExpanded,
    additionalItemInput,
  ]);

  const treeItemsCount = useMemo(
    () => getVisibleNodeCount({ treeData: quoteItems }),
    [quoteItems]
  );
  const [, lastSelectedItemId] = useMemo(
    () => getLastSelectedNode(quoteItems, items, selectedItem),
    [quoteItems, items, selectedItem]
  );

  const onItemsCountChangeLatest = useLatest(onItemsCountChange);

  useUpdateEffect(() => {
    onItemsCountChangeLatest.current?.(treeItemsCount);
  }, [treeItemsCount, onItemsCountChangeLatest]);

  const windowScrollerRef = useRef<any>(null);

  useImperativeHandle(forwardedRef, () => ({
    // isItemExpanded: expandedStore.isItemExpanded,
    // setItemExpanded: expandedStore.setItemExpanded,
    // areAllItemsExpanded: expandedStore.areAllItemsExpanded,
    // setAllItemsExpanded: expandedStore.setAllItemsExpanded,
    // getAllExpandedItemIds: expandedStore.getAllExpandedItemIds,
    updateTree,
    updatePosition: () =>
      windowScrollerRef.current?.updatePosition(
        isVirtualized ? (container ?? undefined) : undefined
      ),
    treeItemsCount,
  }));

  const generateNodeProps = useCallback(
    ({ node }: ExtendedNodeData): VirtualTreeTableContext<T> => {
      return {
        selectedItemId: selectedItem?.id ?? null,
        lastSelectedItemId,
        rootItem,
        items,
        allItems,
        rootItemComponent,
        itemComponent,
        inputComponent,
        EmptyRow,
        HeaderRow,
        headerTitle,
        showExpandButton: showExpandButton ?? (item => item.hasChildren),
      };
    },
    [
      selectedItem?.id,
      lastSelectedItemId,
      rootItem,
      items,
      allItems,
      rootItemComponent,
      itemComponent,
      inputComponent,
      EmptyRow,
      HeaderRow,
      headerTitle,
      showExpandButton,
    ]
  );

  const shouldRenderHeaderRow = !!(headerTitle || HeaderRow);

  const dragging = useRef<boolean>(false);
  const draggingHeight = useRef<number>(0);
  const scrollPaneRef = useRef<{ node: HTMLDivElement } | null>(null);
  const scrollContainerInnerRef = useRef<HTMLDivElement | null>(null);
  const { setChild: setScrollContainerInner, registerChildRef } =
    useWindowScrollerOffsetSync({
      windowScrollerRef,
      childRef: scrollContainerInnerRef,
      scrollElement: container ?? null,
      enabled: isVirtualized,
    });
  const virtualizedListRef = useRef<any>(null);

  const measureScrollContainer = (params: {
    height: number;
    width: number;
  }) => {
    if (!scrollContainerInnerRef.current || !scrollPaneRef.current) return;

    const newScrollbarHeight =
      (scrollPaneRef.current.node.offsetHeight ?? 0) -
      (scrollPaneRef.current.node.clientHeight ?? 0);
    const newContainerWidth = scrollContainerInnerRef.current.clientWidth;
    const newOuterContainerWidth = scrollPaneRef.current.node.clientWidth;

    if (
      (newOuterContainerWidth &&
        newOuterContainerWidth !== outerContainerWidth) ||
      (newContainerWidth && newContainerWidth !== containerWidth) ||
      newScrollbarHeight !== scrollbarHeight
    ) {
      setState({
        outerContainerWidth: newOuterContainerWidth,
        containerWidth: newContainerWidth,
        scrollbarHeight: newScrollbarHeight,
      });
    }
  };

  useElementObserveSize(scrollContainerInnerRef, measureScrollContainer);

  const { startDrag, endDrag } = useScrollZone(container);

  return (
    <LocalDndProvider>
      <div>
        <ScrollSync>
          {/*@ts-ignore render return type is incompatible*/}
          <WindowScroller
            ref={windowScrollerRef}
            scrollElement={isVirtualized ? (container ?? undefined) : undefined}
            onResize={params => {
              measureScrollContainer(params);
              setTimeout(() => {
                windowScrollerRef.current?.updatePosition(
                  isVirtualized ? (container ?? undefined) : undefined
                );
              });
            }}
          >
            {({
              height,
              isScrolling,
              onChildScroll,
              scrollTop,
              registerChild,
            }) => {
              registerChildRef.current = registerChild;
              const showFixedHeaderRow = scrollTop > 9; // 8px top padding + 1px border
              return (
                <>
                  {shouldRenderHeaderRow && showFixedHeaderRow && (
                    <HeaderRowFixedScrollWrapper
                      data-testid="virtual-table-fixed-header"
                      $top={-1}
                    >
                      <ScrollSyncPane group="horizontal">
                        <HeaderRowFixedScrollInner
                          $maxWidth={outerContainerWidth}
                        >
                          <HeaderRowWrapper
                            $minWidth={minWidth}
                            $width={containerWidth}
                          >
                            <Typography>{headerTitle}</Typography>
                            {HeaderRow}
                          </HeaderRowWrapper>
                        </HeaderRowFixedScrollInner>
                      </ScrollSyncPane>
                    </HeaderRowFixedScrollWrapper>
                  )}
                  <ScrollSyncPane
                    group="horizontal"
                    // @ts-ignore
                    ref={scrollPaneRef}
                  >
                    <div
                      className="msys-tree-table-scroll-container"
                      style={{
                        boxSizing: "content-box",
                        position: "relative",
                        height: `${getTreeHeight(treeItemsCount + (shouldRenderHeaderRow ? 1 : 0)) + scrollbarHeight}px`,
                        width: "100%",
                        overflowX: "scroll",
                        overflowY: "hidden",
                        scrollbarGutter: "stable",
                      }}
                    >
                      <div
                        ref={setScrollContainerInner}
                        className="msys-tree-table-scroll-inner"
                        style={{
                          boxSizing: "content-box",
                          position: "relative",
                          overflow: "visible",
                        }}
                      >
                        {shouldRenderHeaderRow && (
                          <HeaderRowWrapper
                            $minWidth={minWidth}
                            $width={containerWidth}
                          >
                            <Typography>{headerTitle}</Typography>
                            {HeaderRow}
                          </HeaderRowWrapper>
                        )}
                        <StyledSortableTree
                          $minWidth={minWidth}
                          style={DEFAULT_STYLE}
                          key={`tree-table-${containerWidth}`}
                          isVirtualized={isVirtualized}
                          generateNodeProps={generateNodeProps}
                          getNodeKey={getTreeNodeKey}
                          reactVirtualizedListProps={{
                            autoHeight: true,
                            height,
                            isScrolling,
                            scrollTop,
                            onScroll: onChildScroll,
                            overscanRowCount: 20,
                            overscanIndicesGetter: ({
                              cellCount,
                              overscanCellsCount,
                              startIndex,
                              stopIndex,
                            }) => {
                              return {
                                overscanStartIndex: Math.max(
                                  0,
                                  startIndex - overscanCellsCount
                                ),
                                overscanStopIndex: Math.min(
                                  cellCount - 1,
                                  stopIndex + overscanCellsCount
                                ),
                              };
                            },
                            ref: virtualizedListRef,
                          }}
                          scaffoldBlockPxWidth={TREE_SCAFFOLD_PX_WIDTH}
                          rowHeight={size.tableItemFullHeight}
                          canDrag={({ node }) =>
                            !moveTreeItemLoading &&
                            enableDragging &&
                            !node.isInput
                          }
                          canDrop={({
                            node,
                            nextParent,
                            nextPath,
                            prevTreeIndex,
                            nextTreeIndex,
                          }) => {
                            if (moveTreeItemLoading) return false;
                            if (
                              dragging.current &&
                              scrollContainerInnerRef.current?.style
                            ) {
                              // calculate new height during dragging
                              const rowCount =
                                virtualizedListRef.current?.props?.rowCount;
                              const newHeight = getTreeHeight(rowCount);
                              if (draggingHeight.current !== newHeight) {
                                draggingHeight.current = newHeight;
                                scrollContainerInnerRef.current.style.height = `${newHeight}px`;
                              }
                            }

                            const nextParentItem = (nextParent?.item ||
                              rootItem) as T;

                            if (
                              "linkedQuoteTemplate" in nextParentItem &&
                              Boolean(nextParentItem.linkedQuoteTemplate)
                            ) {
                              return false;
                            }

                            // an item dropped into an eliminated subtree would
                            // vanish from every view right away
                            if (
                              nextParentItem.isItemEliminated ||
                              ("isAnyParentItemEliminated" in nextParentItem &&
                                nextParentItem.isAnyParentItemEliminated ===
                                  true)
                            ) {
                              return false;
                            }

                            if (
                              !getDefaultCreatableSubItemTypes(
                                nextParentItem.type,
                                documentItemTypes
                              ).includes(node.type)
                            ) {
                              return false;
                            }
                            if (nextParent?.isInput) {
                              return false;
                            }
                            if (
                              syntheticRootItemId === ROOT_ITEM_ID &&
                              (nextParent?.id === syntheticRootItemId ||
                                nextParent?.id === syntheticRootItem?.id)
                            ) {
                              return false;
                            }
                            if (
                              isNodePlacedAfterInput(quoteItems, node, {
                                prevTreeIndex,
                                nextTreeIndex,
                                nextPath,
                              })
                            ) {
                              return false;
                            }
                            return true;
                          }}
                          canNodeHaveChildren={node =>
                            !node.isInput &&
                            allowHaveChildren?.(node.item as T) !== false
                          }
                          treeData={quoteItems}
                          onChange={setQuoteItems}
                          onMoveNode={async ({
                            node,
                            treeData,
                            nextParentNode,
                          }) => {
                            const parentId = (nextParentNode?.id ??
                              rootItem?.id) as string;
                            const visibleSiblingIds = (
                              (nextParentNode?.children as TreeItem[]) ??
                              treeData
                            )
                              .filter(node => !node.isInput)
                              .map(node => node.id as string);
                            const rank = getMoveTreeItemRank({
                              allItems: allItems ?? items,
                              newParentId: parentId,
                              movedItemId: node.id,
                              visibleSiblingIds,
                            });
                            await moveTreeItem?.(node.id, parentId, rank);
                          }}
                          onVisibilityToggle={({ node, expanded }) => {
                            if (expanded === true) {
                              expandedStore.setItemExpanded(node.id, expanded);
                            } else {
                              collapseItemAndChildrenRecursively(node);
                            }
                          }}
                          onDragStateChanged={({ isDragging }) => {
                            if (browserHasOnlyTouch) {
                              if (isDragging) {
                                startDrag();
                              } else {
                                endDrag();
                              }
                            }
                            dragging.current = isDragging;
                            draggingHeight.current = isDragging
                              ? getTreeHeight(treeItemsCount)
                              : 0;
                            if (
                              !isDragging &&
                              scrollContainerInnerRef.current?.style
                            )
                              scrollContainerInnerRef.current.style.height = "";
                          }}
                          // @ts-ignore
                          nodeContentRenderer={VirtualTableNodeRenderer}
                        />
                      </div>
                    </div>
                  </ScrollSyncPane>
                </>
              );
            }}
          </WindowScroller>
        </ScrollSync>
      </div>
    </LocalDndProvider>
  );
}

const getTreeHeight = (itemsCount: number) =>
  size.tableItemFullHeight * itemsCount;

// Takes no layout height (like PageFixedColumn in VirtualItemTree): an in-flow
// header would push the list down when it mounts, which WindowScroller's offset
// sync would feed back into scrollTop and unmount it again (flicker).
const HeaderRowFixedScrollWrapper = styled(
  "div",
  transientOptions
)<{ $top: number }>(
  ({ $top }) => `
  position: sticky;
  height: 0;
  z-index: 20;
  width: 100%;
  top: ${$top}px;
`
);

// The element that scrolls horizontally (ScrollSyncPane syncs this one).
const HeaderRowFixedScrollInner = styled(
  "div",
  transientOptions
)<{ $maxWidth: number }>(
  ({ $maxWidth }) => `
  box-sizing: border-box;
  position: absolute;
  top: 0;
  left: 0;
  height: ${size.tableItemFullHeight}px;
  width: 100%;
  max-width: ${$maxWidth}px;

  overflow-y: visible;
  overflow-x: auto;

  // hiding scrollbar
  &::-webkit-scrollbar {
    display: none;
    width: 0 !important;
  }
  & {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
`
);

const HeaderRowWrapper = styled(
  "div",
  transientOptions
)<{
  $width: number;
  $minWidth: number;
  $overflow?: string;
}>(
  ({ theme, $minWidth, $width }) => `
  height: ${size.tableItemFullHeight}px;
  box-shadow: inset 0 -1px 0 0 ${theme.palette.grey[300]},
    inset 0 1px 0 0 ${theme.palette.grey[300]};

  display: flex;
  align-items: center;
  justify-content: space-between;

  padding-left: 38px;
  padding-right: 6px;
  box-sizing: border-box;

  min-width: ${$minWidth}px;

  background-color: ${color.background};
`
);

const StyledSortableTree = styled(
  SortableTree,
  transientOptions
)<{
  $minWidth: number;
}>(
  ({ theme, $minWidth }) => `
  & {
    margin-left: -${TREE_SCAFFOLD_PX_WIDTH}px;
    min-width: ${$minWidth + TREE_SCAFFOLD_PX_WIDTH}px;
  }

  &.rst__tree {
    flex-grow: 1;
    min-height: 0;
  }

  /**
   * Extra class applied to VirtualScroll through className prop
   */
  .rst__virtualScrollOverride {
    overflow: visible !important;
  }

  .ReactVirtualized__Grid__innerScrollContainer {
    overflow: visible !important;
  }

  .ReactVirtualized__Grid {
    outline: none;
  }

  .rst__node {
    min-width: 100%;
    white-space: nowrap;
    text-align: left;
    position: relative;
    // border-top: 1px solid rgba(0, 0, 0, 0.12);
  }

  .rst__nodeContent {
    box-sizing: border-box;
    padding: 0;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
  }
`
);
